'use client';

/**
 * Magazine-style horizontal card. 180px image on the left, body on
 * the right with title (3-line clamp), instructor row, meta strip
 * (duration · enrollment · cert badge). 4px brand stripe down the
 * left edge.
 */
import { useTranslation } from 'react-i18next';
import { PortalLink } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { CourseProgressBar } from '../_shared/CourseProgressBar';
import { CourseCoverImage } from '../_shared/CourseCoverImage';
import { cardReset } from '../_shared/styles';
import type { CourseCardProps } from './CourseCard.types';
import type { CardGridSpec } from './gridSpecs';
import type { ProgressTextPlace } from './registry';

const muted = { color: 'var(--color-text-secondary, #5f6675)' };
const cardSurface = {
  background: 'var(--color-surface-card, #ffffff)',
  borderRadius: 'var(--radius-md, 12px)',
  border: '1px solid var(--color-border-subtle, #e5e7eb)',
  boxShadow: 'var(--shadow-xs, 0 1px 2px rgba(0,0,0,0.08))',
};

/** Known course-type codes → existing common.json i18n keys. Mirrors the
 *  portal's learningTypeLabel mapping (REMOTE_COURSE → e-learning,
 *  F2F_COURSE → in-class, …). A kicker that isn't a known code is treated
 *  as free-form editorial text and rendered as-is. */
const KICKER_LABEL_KEYS: Record<string, string> = {
  REMOTE_COURSE: 'title.eLearning',
  F2F_COURSE: 'title.inClass',
  PROGRAM: 'title.program',
  plan: 'label.developmentPlanLearning',
};

export function WideCourseCard({
  course: c,
  progressBar: {
    show: showProgress = false,
    showText: showProgressText = false,
    textPlace: progressTextPlace = 'outside-right',
  } = {},
}: {
  course: CourseCardProps;
  progressBar?: { show?: boolean; showText?: boolean; textPlace?: ProgressTextPlace };
}) {
  const { t } = useTranslation('common');

  // Kicker eyebrow: prefer the course-type/kicker code, else the level.
  // Known enum codes resolve to their shared label; a level falls back to
  // its `label.<level>` key; anything else is free-form and passes through.
  const kickerKey = c.kicker ? KICKER_LABEL_KEYS[c.kicker] : undefined;
  const kickerLabel = c.kicker
    ? kickerKey
      ? t(kickerKey)
      : c.kicker
    : c.level
      ? t(`label.${c.level.toLowerCase()}`, c.level)
      : undefined;
  return (
    <Card asChild>
      <PortalLink
        href={c.href}
        style={{
          ...cardReset,
          ...cardSurface,
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: '180px 1fr',
          overflow: 'hidden',
          color: 'inherit',
          textDecoration: 'none',
          transition: 'transform 200ms ease, box-shadow 200ms ease',
          backgroundImage:
            'linear-gradient(to right, var(--color-brand-500, #3b5bdb) 0, var(--color-brand-500, #3b5bdb) 4px, transparent 4px)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateX(2px)';
          e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.10)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = '';
          e.currentTarget.style.boxShadow = '';
        }}
      >
        <div
          style={{
            marginLeft: 4,
            backgroundColor: 'var(--color-neutral-100, #f4f4f5)',
            aspectRatio: 'var(--app-course-image-aspect-ratio, 16 / 10)',
            alignSelf: 'start',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <CourseCoverImage
            image={typeof c.image === 'string' ? c.image : undefined}
            resizeTo="360x0"
            sizes="180px"
            gradient="linear-gradient(135deg, var(--color-neutral-200, #e5e7eb), var(--color-neutral-100, #f4f4f5))"
          />
        </div>
        <div
          style={{
            padding: 'clamp(14px, 1.8vw, 18px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            minWidth: 0,
          }}
        >
          {kickerLabel && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-widest, 0.08em)',
                textTransform: 'uppercase',
                color: 'var(--color-brand-600, #3b5bdb)',
              }}
            >
              {kickerLabel}
            </div>
          )}
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-md, 16px)',
              fontWeight: 700,
              lineHeight: 1.3,
              color: 'var(--color-text-primary, #0f1218)',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {c.title}
          </h3>
          {/* {c.instructor && (
          <div style={{ ...muted, fontSize: '13px' }}>
            {t('label.by')} <span style={{ fontWeight: 600 }}>{c.instructor}</span>
          </div>
        )} */}
          <div
            style={{
              ...muted,
              marginTop: 'auto',
              paddingTop: 4,
              fontSize: 'var(--font-size-xs, 12px)',
              display: 'flex',
              gap: 14,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {c.duration && <span>⏱ {c.duration}</span>}
            {typeof c.enrolled === 'number' && (
              <span>
                {c.enrolled.toLocaleString()} {t('label.participant')}
              </span>
            )}
            {c.hasCertificate && (
              <span
                style={{
                  color: 'var(--color-brand-600, #1d4ed8)',
                  fontWeight: 700,
                  fontSize: '11px',
                  padding: 'var(--space-0-5, 2px) var(--space-1-5, 6px)',
                  background: 'var(--color-brand-50, rgba(59,91,219,0.08))',
                  borderRadius: 3,
                  textTransform: 'uppercase',
                }}
              >
                {t('label.certified')}
              </span>
            )}
          </div>
          {showProgress && typeof c.progress === 'number' && c.progress > 0 && (
            <div style={{ marginTop: 8 }}>
              <CourseProgressBar
                progress={c.progress}
                showText={showProgressText}
                textPlace={progressTextPlace}
              />
            </div>
          )}
        </div>
      </PortalLink>
    </Card>
  );
}

WideCourseCard.gridSpec = {
  kind: 'grid',
  minCardWidth: 420,
  gap: 'clamp(14px, 2vw, 20px)',
} satisfies CardGridSpec;
